import { createContext, useContext, useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const CrmDealDrawer = lazyWithReload(() => import('./CrmDealDrawer'), 'CrmDealDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

/**
 * URL-driven `?dealId=<short>`.
 * API: useCrmDealDrawer() → { openDeal, closeDeal, version, bumpVersion, openDealId }
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openDeal: noProviderOpener('deal'),
  closeDeal: () => {},
  version: 0,
  bumpVersion: () => {},
  openDealId: null,
};

function CrmDealDrawerProviderOn({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const dealId = searchParams.get('dealId');
  const [version, setVersion] = useState(0);

  const openDeal = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('dealId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('dealId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeDeal = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('dealId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('dealId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('deal', openDeal), [openDeal]);

  return (
    <Ctx.Provider value={{ openDeal, closeDeal, version, bumpVersion, openDealId: dealId }}>
      {children}
      {dealId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeDeal} />}>
          <CrmDealDrawer open={!!dealId} dealId={dealId} onClose={closeDeal} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useCrmDealDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}

// Cụm CRM tắt ở host (`modules.crm = false` — Site Connect 12/10/2026) ⇒ Provider rỗng: không đăng ký popup, không
// đọc query param, không dựng drawer (nội dung drawer gọi hàng chục /crm/*). Xem lib/hostModules.
export const CrmDealDrawerProvider = providerIfModuleOn('crm', CrmDealDrawerProviderOn);
